'use client';

import Image from 'next/image'
import { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import AsteriskHandDrawn from '../assets/AsteriskHandDrawn';
import Shine from '../assets/Shine';

type HeroCareersProps = {
  id: string;
};

export default function HeroCareers({ id }: HeroCareersProps) {
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [hasFadedOut, setHasFadedOut] = useState(false);
  const [isInView, setIsInView] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const isAnimatingRef = useRef(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const heroTextRef = useRef<HTMLHeadingElement>(null);
  const heroTextMobileRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const iconsDesktopRef = useRef<HTMLHeadingElement>(null);
  const iconsMobileRef = useRef<HTMLHeadingElement>(null);
  const asteriskDesktopRef = useRef<SVGSVGElement>(null);
  const asteriskMobileRef = useRef<SVGSVGElement>(null);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Track screen size changes
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer to track if hero is in view
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (hasFadedOut) return; // Once faded out, stop listening
      
      const scrollY = window.scrollY;
      // Fade out over first 200px of scroll
      const opacity = Math.max(0, 1 - scrollY / 200);
      setScrollOpacity(opacity);
      
      // Lock it at 0 once fully faded
      if (opacity === 0) {
        setHasFadedOut(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasFadedOut]);

  // Update ref when state changes
  useEffect(() => {
    isAnimatingRef.current = isAnimating;
  }, [isAnimating]);

  // Continuous random animation between Ivy italic and Ivy normal
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    // Only get spans for the current screen size
    const spans = isMobile && heroTextMobileRef.current
      ? Array.from(heroTextMobileRef.current.querySelectorAll('span.hero-char'))
      : !isMobile && heroTextRef.current
      ? Array.from(heroTextRef.current.querySelectorAll('span.hero-char'))
      : [];
    
    if (spans.length === 0) return;
    
    // Set initial state - characters with 'italic' class should be italic, others normal
    spans.forEach(span => {
      if (span.classList.contains('italic')) {
        gsap.set(span, { fontStyle: 'italic' });
      } else {
        gsap.set(span, { fontStyle: 'normal' });
      }
    });

    const animateRandomChars = () => {
      // Separate spans into italic and normal
      const italicSpans = spans.filter(span => 
        window.getComputedStyle(span).fontStyle === 'italic'
      );
      const _normalSpans = spans.filter(span => 
        window.getComputedStyle(span).fontStyle === 'normal'
      );

      // Pick 1 random character to animate
      const selected = [];

      // If we're at or over the limit, pick from italic chars to turn normal
      if (italicSpans.length >= 4) {
        const shuffled = [...italicSpans].sort(() => 0.5 - Math.random());
        selected.push(shuffled[0]);
      } 
      // If we have room, pick randomly from all chars
      else {
        const shuffled = [...spans].sort(() => 0.5 - Math.random());
        selected.push(shuffled[0]);
      }

      selected.forEach((span) => {
        const currentStyle = window.getComputedStyle(span).fontStyle;
        const newStyle = currentStyle === 'italic' ? 'normal' : 'italic';
        
        // Double-check we don't exceed limit
        if (newStyle === 'italic' && italicSpans.length >= 4) {
          return;
        }
        
        gsap.to(span, {
          fontStyle: newStyle,
          duration: 0.4,
          ease: 'power2.inOut'
        });
      });
    };

    let timeoutId: NodeJS.Timeout | null = null;
    
    const scheduleNext = () => {
      const randomDelay = Math.random() * 25 + 150;
      timeoutId = setTimeout(() => {
        // Check ref for current animation state (not closure value)
        if (isInView && isAnimatingRef.current) {
          animateRandomChars();
          scheduleNext();
        }
      }, randomDelay);
    };
    
    // Start scheduling if animation is active
    if (isAnimating) {
      scheduleNext();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInView, isMobile, isAnimating, prefersReducedMotion]);

  // Stop animation after 5 seconds and restore initial italic state
  useEffect(() => {
    // Don't run timer if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    const stopTimeoutId = setTimeout(() => {
      setIsAnimating(false);
      
      // Restore initial italic state when animation stops
      const allSpans = isMobile && heroTextMobileRef.current
        ? Array.from(heroTextMobileRef.current.querySelectorAll('span.hero-char'))
        : !isMobile && heroTextRef.current
        ? Array.from(heroTextRef.current.querySelectorAll('span.hero-char'))
        : [];
      
      const spans = allSpans.filter(span => !span.classList.contains('no-animate'));
      
      spans.forEach(span => {
        if (span.classList.contains('italic')) {
          gsap.to(span, {
            fontStyle: 'italic',
            duration: 0.4,
            ease: 'power2.inOut'
          });
        } else {
          gsap.to(span, {
            fontStyle: 'normal',
            duration: 0.4,
            ease: 'power2.inOut'
          });
        }
      });
    }, 5000);

    return () => {
      clearTimeout(stopTimeoutId);
    };
  }, [isMobile, prefersReducedMotion]); // Include isMobile to handle screen size changes

  // Icon display properties setup (no rotation animation)
  useEffect(() => {
    // Only get icons for the current screen size
    const iconContainer = isMobile ? iconsMobileRef.current : iconsDesktopRef.current;
    if (!iconContainer) return;

    const icons = Array.from(iconContainer.querySelectorAll('.hero-icon'));
    if (icons.length === 0) return;

    // Set display properties to help with centering and clear any rotation
    icons.forEach(icon => {
      const element = icon as HTMLElement;
      element.style.display = 'inline-flex';
      element.style.alignItems = 'center';
      element.style.justifyContent = 'center';
    });
    
    // Explicitly clear any GSAP rotation transforms
    gsap.set(icons, { rotation: 0, clearProps: 'transform' });
  }, [isMobile]);

  // Rotate asterisk with randomized rotation and timing
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      return;
    }
    
    const asterisk = isMobile ? asteriskMobileRef.current : asteriskDesktopRef.current;
    if (!asterisk) return;

    // Set initial rotation and transform origin (center for rotation)
    gsap.set(asterisk, { rotation: 0, transformOrigin: '50% 50%' });

    const rotateAsterisk = () => {
      const currentRotation = gsap.getProperty(asterisk, 'rotation') as number;
      // Random rotation between -90 and 90 degrees
      const randomRotation = (Math.random() - 0.5) * 180;
      const newRotation = currentRotation + randomRotation;

      // Snap instantly (no animation)
      gsap.set(asterisk, { rotation: newRotation });
    };

    let timeoutId: NodeJS.Timeout | null = null;

    const scheduleNext = () => {
      // More randomized delay between 500ms and 2000ms
      const randomDelay = Math.random() * 1500 + 500;
      timeoutId = setTimeout(() => {
        // Check ref for current animation state (not closure value)
        if (isInView && isAnimatingRef.current) {
          rotateAsterisk();
          scheduleNext();
        }
      }, randomDelay);
    };

    // Start scheduling if animation is active
    if (isAnimating) {
      scheduleNext();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInView, isMobile, isAnimating, prefersReducedMotion]);

  return (
    <section ref={sectionRef} className="careers-hero relative min-h-[100vh] lg:min-h-[100vh] flex flex-col items-center pt-16 pb-12 lg:pt-24 lg:pb-2" id={id}>
      
      {/* Desktop version */}
      <div className="animated-text-desktop hidden mt-10 lg:flex w-full max-w-120rem flex-1 items-center justify-center">
        <h1 
          ref={(el) => { heroTextRef.current = el; iconsDesktopRef.current = el; }} 
          className="heading-1-lg max-w-[85%] mx-auto careers-text-container" 
          aria-label="Make work that moves the world"
          onMouseEnter={() => setIsAnimating(true)}
          onMouseLeave={() => setIsAnimating(false)}
        >
          <span className="hero-char">M</span>
          <span className="hero-char">a</span>
          <span className="hero-char shine-char-wrapper">
            <span className="hero-char">k</span>
            <span className="shine-wrapper">
              <span className="shine-icon">
                <Shine />
              </span>
            </span>
          </span>
          <span className="hero-char italic">e</span>
          <span className="inline-block w-5"></span>
          <span className="hero-char">w</span>
          <span className="hero-char italic">o</span>
          <span className="hero-char">r</span>
          <span className="hero-char">k</span>
          <span className="inline-block w-5"></span>
          <span className="hero-char">t</span>
          <span className="hero-char">h</span>
          <span className="hero-char">a</span>
          <span className="hero-char">t</span>
          <span className="inline-block w-5"></span>
          <span className="hero-char">m</span>
          <span className="hero-char">o</span>
          <span className="hero-char">v</span>
          <span className="hero-char">e</span>
          <span className="hero-char">s</span>

          <span className="hero-line-break"></span>
          
          <span className="whitespace-nowrap">
            <span className="hero-char">t</span>
            <span className="hero-char">h</span>
            <span className="hero-char italic">e</span>
            <span className="inline-block w-5"></span>
            <span className="hero-char">w</span>
            <span className="hero-char">o</span>
            <span className="hero-char">r</span>
            <span className="hero-char">l</span>
            <span className="hero-char">d</span>
            <span className="hero-char">.</span>
            <span className="careers-asterisk-wrapper"><span className="hero-icon inline-flex items-center justify-center"><AsteriskHandDrawn className="careers-asterisk" svgRef={asteriskDesktopRef} /></span></span>
          </span>
        </h1>
      </div>


      {/* Mobile version */}
      <div className="animated-text-mobile lg:hidden w-full max-w-120rem global-padding mx-auto flex-1 flex items-center justify-center pb-[3.125rem] md:pb-[1.875rem]">
        <h1 
          ref={(el) => { heroTextMobileRef.current = el; iconsMobileRef.current = el; }} 
          className="heading-1-lg careers-text-container" 
          aria-label="Make work that moves the world"
          onMouseEnter={() => setIsAnimating(true)}
          onMouseLeave={() => setIsAnimating(false)}
        >
          <span className="hero-char">M</span>
          <span className="hero-char">a</span>
          <span className="hero-char shine-char-wrapper">
            <span className="hero-char">k</span>
            <span className="shine-wrapper">
              <span className="shine-icon">
                <Shine />
              </span>
            </span>
          </span>
          <span className="hero-char italic">e</span>
          <span className="inline-block careers-mobile-spacer"></span>
          <span className="hero-char">w</span>
          <span className="hero-char italic">o</span>
          <span className="hero-char">r</span>
          <span className="hero-char">k</span>

          <span className="hero-line-break"></span>

          <span className="hero-char">t</span>
          <span className="hero-char">h</span>
          <span className="hero-char">a</span>
          <span className="hero-char">t</span>
          <span className="inline-block careers-mobile-spacer"></span>
          <span className="hero-char">m</span>
          <span className="hero-char">o</span>
          <span className="hero-char">v</span>
          <span className="hero-char">e</span>
          <span className="hero-char">s</span>

          <span className="hero-line-break"></span>
          
          <span className="whitespace-nowrap">
            <span className="hero-char">t</span>
            <span className="hero-char">h</span>
            <span className="hero-char italic">e</span>
            <span className="inline-block careers-mobile-spacer"></span>
            <span className="hero-char">w</span>
            <span className="hero-char">o</span>
            <span className="hero-char">r</span>
            <span className="hero-char">l</span>
            <span className="hero-char">d</span>
            <span className="hero-char">.</span>
            <span className="careers-asterisk-wrapper"><span className="hero-icon inline-flex items-center justify-center"><AsteriskHandDrawn className="careers-asterisk" svgRef={asteriskMobileRef} /></span></span>
          </span>
        </h1>
      </div>


      <div 
        className={`scroll-for-more hidden lg:flex flex-col items-center gap-2 transition-opacity duration-300 cursor-pointer mt-12 mb-4 ${scrollOpacity === 0 ? 'pointer-events-none' : ''}`}
        style={{ opacity: scrollOpacity }}
        onClick={() => {
          window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
          });
        }}
      >
        <p className="text-center !text-[0.75rem] uppercase">SCROLL FOR MORE</p>
        <Image src="/images/underline.svg" alt="" width={160} height={12} className="mx-auto" priority />
      </div>
    </section>
  );
}
